import type { Appointment, TaskListItem, TimeManagementListItem } from '@myapp/contract'

const BREAK_DURATION_MINUTES = 30

export interface LaneInterval {
  id: string | number
  startMs: number
  endMs: number
}

export interface LaneAssignment {
  laneIndex: number
  laneCount: number
}

export interface BuildRowIntervalsOptions {
  timeManagementEntries?: readonly TimeManagementListItem[]
  breakBlock?: { startTimeIso: string } | null
  dayIso?: string
  shiftStartHour?: number | null
  shiftEndHour?: number | null
}

function getClampBoundsMs(
  dayIso: string,
  shiftStartHour: number | null | undefined,
  shiftEndHour: number | null | undefined,
): { clampStartMs: number; clampEndMs: number } {
  const base = new Date(dayIso)
  const clampStart = new Date(base)
  clampStart.setHours(
    shiftStartHour != null ? Math.floor(shiftStartHour) : 0,
    shiftStartHour != null ? Math.round((shiftStartHour % 1) * 60) : 0,
    0, 0,
  )
  const clampEnd = new Date(base)
  clampEnd.setHours(
    shiftEndHour != null ? Math.floor(shiftEndHour) : 23,
    shiftEndHour != null ? Math.round((shiftEndHour % 1) * 60) : 59,
    shiftEndHour != null ? 0 : 59,
    shiftEndHour != null ? 0 : 999,
  )
  return { clampStartMs: clampStart.getTime(), clampEndMs: clampEnd.getTime() }
}

export function buildRowIntervals(
  appointments: readonly Appointment[],
  timedTasks: readonly TaskListItem[],
  options: BuildRowIntervalsOptions = {},
): { id: string; startMs: number; endMs: number }[] {
  const out: { id: string; startMs: number; endMs: number }[] = []

  for (const appt of appointments) {
    const workStart = new Date(appt.actualStart ?? appt.plannedStart).getTime()
    const travel = appt.actualTravelDuration ?? appt.plannedTravelDuration
    const work = appt.actualWorkDuration ?? appt.plannedWorkDuration
    const departure = appt.actualDepartureDuration ?? appt.plannedDepartureDuration
    out.push({
      id: `a:${appt.id}`,
      startMs: workStart - travel * 60_000,
      endMs: workStart + (work + departure) * 60_000,
    })
  }

  for (const task of timedTasks) {
    const useActuals = task.status === 'COMPLETED' || (!!task.actualStart && !task.actualEnd)
    const travel = useActuals ? (task.actualTravelDuration ?? task.plannedTravelDuration) : task.plannedTravelDuration
    const work = useActuals ? (task.actualWorkDuration ?? task.plannedWorkDuration) : task.plannedWorkDuration
    const departure = useActuals ? (task.actualDepartureDuration ?? task.plannedDepartureDuration) : task.plannedDepartureDuration
    const workStartIso = useActuals && task.actualStart ? task.actualStart : task.plannedStart
    if (!workStartIso) continue
    const workStart = new Date(workStartIso).getTime()
    out.push({
      id: `t:${task.id}`,
      startMs: workStart - travel * 60_000,
      endMs: workStart + (work + departure) * 60_000,
    })
  }

  const { timeManagementEntries, breakBlock, dayIso, shiftStartHour, shiftEndHour } = options

  if (timeManagementEntries?.length && dayIso) {
    const { clampStartMs, clampEndMs } = getClampBoundsMs(dayIso, shiftStartHour, shiftEndHour)
    for (const entry of timeManagementEntries) {
      const startMs = Math.max(new Date(entry.startTime).getTime(), clampStartMs)
      const endMs = Math.min(new Date(entry.endTime).getTime(), clampEndMs)
      if (endMs <= startMs) continue
      out.push({ id: `tm:${entry.id}`, startMs, endMs })
    }
  }

  if (breakBlock) {
    const startMs = new Date(breakBlock.startTimeIso).getTime()
    out.push({
      id: 'br',
      startMs,
      endMs: startMs + BREAK_DURATION_MINUTES * 60_000,
    })
  }

  return out
}

export function getMaxLaneCount(laneMap: ReadonlyMap<unknown, LaneAssignment>): number {
  let max = 1
  laneMap.forEach(({ laneCount }) => {
    if (laneCount > max) max = laneCount
  })
  return max
}

export function computeLanes<T extends LaneInterval>(intervals: T[]): Map<T['id'], LaneAssignment> {
  const result = new Map<T['id'], LaneAssignment>()
  if (intervals.length === 0) return result

  const sorted = [...intervals].sort((a, b) => a.startMs - b.startMs || a.endMs - b.endMs)
  const laneEnds: number[] = []
  const laneByIndex: number[] = new Array(sorted.length)

  for (let i = 0; i < sorted.length; i++) {
    const cur = sorted[i]
    let placed = -1
    for (let li = 0; li < laneEnds.length; li++) {
      if (laneEnds[li] <= cur.startMs) {
        laneEnds[li] = cur.endMs
        placed = li
        break
      }
    }
    if (placed === -1) {
      laneEnds.push(cur.endMs)
      placed = laneEnds.length - 1
    }
    laneByIndex[i] = placed
  }

  const clusters: number[][] = []
  {
    const order = sorted.map((_, i) => i).sort((a, b) => sorted[a].startMs - sorted[b].startMs)
    let cluster: number[] = []
    let clusterEnd = -Infinity
    for (const i of order) {
      const s = sorted[i]
      if (cluster.length === 0 || s.startMs < clusterEnd) {
        cluster.push(i)
        clusterEnd = Math.max(clusterEnd, s.endMs)
      } else {
        clusters.push(cluster)
        cluster = [i]
        clusterEnd = s.endMs
      }
    }
    if (cluster.length) clusters.push(cluster)
  }

  for (const cluster of clusters) {
    const laneCount = cluster.reduce((max, i) => Math.max(max, laneByIndex[i] + 1), 0)
    for (const i of cluster) {
      result.set(sorted[i].id, { laneIndex: laneByIndex[i], laneCount })
    }
  }

  return result
}
